import type {
  CustomerTaskDefinition,
  CustomerTaskInputDefinition,
  CustomerTaskIssueDefinition,
  CustomerTaskSelectDefinition,
} from '../types/customerTask';

const RENDERABLE_TASK_TYPES: ReadonlySet<string> = new Set(['info', 'complete_link', 'put_customer_info', 'select', 'free_text_response']);
const MULTI_STEP_TASK_TYPES: ReadonlySet<string> = new Set(['put_customer_info', 'select', 'free_text_response']);

export function getCustomerIssueTask<TTask extends CustomerTaskDefinition>(
  issue: CustomerTaskIssueDefinition<TTask>
): TTask | undefined {
  return issue.task ?? issue.tasks?.[0];
}

export function getCustomerIssueTasks<TTask extends CustomerTaskDefinition>(
  issue: CustomerTaskIssueDefinition<TTask>
): TTask[] {
  return issue.tasks ?? (issue.task ? [issue.task] : []);
}

export function getCustomerMultiStepTasks<TTask extends CustomerTaskDefinition>(
  tasks?: TTask[]
): Extract<TTask, CustomerTaskInputDefinition | CustomerTaskSelectDefinition>[] {
  if (!tasks) return [];
  return tasks.filter(
    (task): task is Extract<TTask, CustomerTaskInputDefinition | CustomerTaskSelectDefinition> =>
      MULTI_STEP_TASK_TYPES.has(task.taskType)
  );
}

export function isRenderableCustomerTaskIssue<TTask extends CustomerTaskDefinition>(
  issue: CustomerTaskIssueDefinition<TTask>
): boolean {
  if (issue.tasks && issue.tasks.length > 1) return getCustomerMultiStepTasks(issue.tasks).length > 0;
  const task = getCustomerIssueTask(issue);
  return task ? RENDERABLE_TASK_TYPES.has(task.taskType) : false;
}

export function getUnregisteredCustomerTaskTypes<TTask extends CustomerTaskDefinition>(
  issues: CustomerTaskIssueDefinition<TTask>[]
): string[] {
  const unregistered = new Set<string>();
  for (const issue of issues) {
    for (const task of [issue.task, ...(issue.tasks ?? [])]) {
      if (task && !RENDERABLE_TASK_TYPES.has(task.taskType)) unregistered.add(task.taskType);
    }
  }
  return Array.from(unregistered);
}
